import { useEffect, useState } from 'react';
import { RequestState } from '../../@types/RequestState';
import { CMS, Cars, MultiSearch, SearchIndex } from './SearchModels';
import { SearchType } from './useSearch';

const useMultiSearch = (
  url: string,
  urlVehicle: string,
  urlContent: string,
  query?: string,
  disabled?: boolean
): [MultiSearch | undefined, RequestState, (searchType?: SearchType) => void] => {
  const [multiSearch, setMultiSearch] = useState<MultiSearch | undefined>();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (query !== undefined && !disabled) {
      onFetch(query);
    }
  }, [query, disabled]);

  const onFetch = async (_query: string) => {
    try {
      setIsLoading(true);
      setIsError(false);
      setIsSuccess(false);
      const response = await fetch(`${url}?query=${_query}&hits=30`);
      const data = (await response.json()) as MultiSearch;

      setMultiSearch(data);
      setIsSuccess(true);
    } catch (_e) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreCars = async () => {
    if (multiSearch) {
      try {
        setIsLoading(true);
        setIsError(false);
        setIsLoadingMore(true);
        const urlSearchParams = new URLSearchParams(
          multiSearch.vehicle?.documentList.pagination.nextPage?.query || ''
        );

        const response = await fetch(`${urlVehicle}?${urlSearchParams}`);
        const result = (await response.json()) as SearchIndex<Cars>;
        const nextCars: SearchIndex<Cars> = {
          ...result,
          documentList: {
            ...result.documentList,
            documents: [
              ...multiSearch.vehicle.documentList.documents,
              ...result.documentList.documents,
            ],
          },
        };
        setMultiSearch({
          ...multiSearch,
          vehicle: nextCars,
        });

        setIsSuccess(false);
      } catch (_e) {
        setIsError(true);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  };

  const loadMoreContent = async () => {
    if (multiSearch) {
      try {
        setIsLoading(true);
        setIsError(false);
        setIsLoadingMore(true);
        const urlSearchParams = new URLSearchParams(
          multiSearch.retailer_cms?.documentList.pagination.nextPage?.query || ''
        );
        const nextUrlSearchParams = new URLSearchParams();
        const hits = urlSearchParams.get('hits');
        if (hits) {
          nextUrlSearchParams.set('hits', hits);
        }
        const offset = urlSearchParams.get('offset');
        if (offset) {
          nextUrlSearchParams.set('offset', offset);
        }

        const query = new URLSearchParams(location.search).get('query');
        if (query) {
          nextUrlSearchParams.set('query', query);
        }

        const response = await fetch(`${urlContent}?${nextUrlSearchParams}`);
        const result = (await response.json()) as SearchIndex<CMS>;
        const nextContents: SearchIndex<CMS> = {
          ...result,
          documentList: {
            ...result.documentList,
            documents: [
              ...multiSearch.retailer_cms.documentList.documents,
              ...result.documentList.documents,
            ],
          },
        };
        setMultiSearch({
          ...multiSearch,
          retailer_cms: nextContents,
        });

        setIsSuccess(false);
      } catch (_e) {
        setIsError(true);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  };

  const onLoadMore = (searchType?: SearchType) => {
    if (searchType === 'retailers_cms') {
      loadMoreContent();
    } else {
      loadMoreCars();
    }
  };

  return [
    multiSearch,
    {
      isLoading,
      isLoadingMore,
      isSuccess,
      isError,
    },
    onLoadMore,
  ];
};

export default useMultiSearch;
